/// <reference no-default-lib="true" />
/// <reference lib="esnext" />
/// <reference lib="dom" />
import {
  type BreakpointName,
  breakpoints,
  type ResponsiveImageConfig,
} from '../../design/breakpoints.ts'

export const SIZES = {
  b0: { width: 359, height: 449 },
  b360: { width: 411, height: 514 },
  lighthouse: { width: 412, height: 515 },
  b440: { width: 479, height: 599 },
  b480: { width: 679, height: 849 },
  b680: { width: 767, height: 959 },
  b768: { width: 991, height: 1239 },
  b992: { width: 1279, height: 719 },
  b1280: { width: 1679, height: 944 },
  b1680: { width: 1919, height: 1079 },
  b1920: { width: 1920, height: 1080 },
} satisfies ResponsiveImageConfig

export const SIZES_STAGE = {
  b0: { width: 359, height: 638 },
  b360: { width: 411, height: 605 },
  lighthouse: { width: 412, height: 607 },
  b440: { width: 479, height: 705 },
  b480: { width: 679, height: 812 },
  b680: { width: 767, height: 828 },
  b768: { width: 991, height: 964 },
  b992: { width: 1279, height: 548 },
  b1280: { width: 1679, height: 720 },
  b1680: { width: 1919, height: 822 },
  b1920: { width: 1920, height: 823 },
} satisfies ResponsiveImageConfig

function getCurrentSize(stage = false) {
  const sizes = stage ? SIZES_STAGE : SIZES
  for (const [key, size] of Object.entries(sizes).reverse()) {
    const min = breakpoints[key as BreakpointName].min
    if (min > 0 && window.matchMedia(`(min-width: ${min}px)`).matches) {
      return size
    }
  }
  return sizes.b0
}

function createCloudinaryUrl(url: string, width: number, height: number) {
  const encoded = encodeURIComponent(url)
  return `https://res.cloudinary.com/sonax-gmbh/video/fetch/w_${width},h_${height},c_fill,g_center,q_auto:best,f_auto,dpr_auto/${encoded}`
}

function resolveVideoSrc(rawSrc: string, stage: boolean) {
  const url = new URL(rawSrc, window.location.href)
  if (url.hostname.endsWith('shopify.com')) {
    return rawSrc
  }
  const { width, height } = getCurrentSize(stage)
  return createCloudinaryUrl(rawSrc, width, height)
}

function activateVideo(video: HTMLElement, videoEl: HTMLVideoElement) {
  const rawSrc = videoEl.dataset.src
  if (!rawSrc || videoEl.src) return
  const stage = !!video.closest('.stage')

  videoEl.src = resolveVideoSrc(rawSrc, stage)
  videoEl.addEventListener('canplay', () => {
    videoEl.autoplay = true
    videoEl.play()

    setTimeout(() => {
      video.classList.remove('video--placeholder')
    }, 50)
  }, { once: true })
  videoEl.load()
}

export function initVideo() {
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)',
  )
  const videos = document.querySelectorAll('.video')
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (prefersReducedMotion.matches) return
        if (!entry.isIntersecting) return
        const video = entry.target as HTMLElement
        const videoEl = video.querySelector('.video__video') as
          | HTMLVideoElement
          | null
        if (videoEl) {
          activateVideo(video, videoEl)
        }
        observer.unobserve(video)
      })
    },
    { rootMargin: '200px 0px' },
  )
  videos.forEach((video) => {
    if (!(video instanceof HTMLElement)) return
    if (video.dataset.hydrated) return
    video.dataset.hydrated = 'true'
    const videoEl = video.querySelector('.video__video') as
      | HTMLVideoElement
      | null
    const controlButton = video.querySelector('.video__controls-play-pause') as
      | HTMLButtonElement
      | null
    const toggle = controlButton?.querySelector('.icon__toggle') as
      | HTMLElement
      | null
    if (!videoEl || !controlButton || !toggle) return
    const toggleIcon = (isPlaying: boolean) => {
      const eventName = isPlaying ? 'toggle-default' : 'toggle-toggle'
      toggle.dispatchEvent(new CustomEvent(eventName))
      controlButton.setAttribute(
        'aria-label',
        isPlaying ? 'Pause video' : 'Play video',
      )
    }
    const isPlaying = !videoEl.paused
    toggleIcon(isPlaying)
    controlButton.addEventListener('click', () => {
      if (videoEl.paused) {
        videoEl.play()
      } else {
        videoEl.pause()
      }
    })
    videoEl.addEventListener('play', () => toggleIcon(true))
    videoEl.addEventListener('pause', () => toggleIcon(false))
    observer.observe(video)

    const scrollDown = video.querySelector<HTMLAnchorElement>(
      '.video__controls-action a',
    )

    if (scrollDown?.hash) {
      scrollDown.addEventListener('click', (e) => {
        e.preventDefault()
        const target = document.querySelector(scrollDown.hash)
        target?.scrollIntoView({
          behavior: prefersReducedMotion.matches ? 'instant' : 'smooth',
        })
      })
    }
  })
}
